import { stripLocalePrefix } from '@/i18n/locale-path';

/**
 * Is `href` the nav entry for the page currently on screen?
 *
 * ## Why this is a function rather than two inline comparisons
 *
 * It used to be one expression, written twice — once in the bar and once in
 * the menu panel — and it compared `useLocation().pathname` directly against
 * the entry's href. Those hrefs are English-relative (`/features`), and the
 * pathname is the REAL one, which on every non-English locale carries its
 * prefix (`/de/features`). So the comparison was false for five of the six
 * locales, and the nav simply had no current page on any of them: no
 * `aria-current`, so a screen-reader user lost their "you are here" landmark
 * entirely, and no highlight, so a sighted one lost it too.
 *
 * Nothing could have caught it from the English side — the English path has no
 * prefix to strip, so the broken expression and the correct one agree exactly
 * there. It is the `quality-bar.md` § "Multilingual Is Part of Done" failure
 * shape: not a missing string, a behaviour that is only correct in one
 * language. `nav-active.test.ts` pins it with a non-English case.
 *
 * ## The prefix match is on a SEGMENT boundary
 *
 * `startsWith` alone would make `/features` the current page for a
 * `/features-of-something` route. Comparing against `${href}/` keeps a child
 * page (`/docs/quickstart` under `/docs`) current while a sibling that merely
 * shares a spelling is not.
 */
export function isNavLinkActive(pathname: string, href: string): boolean {
  const path = stripLocalePrefix(pathname);
  return path === href || path.startsWith(`${href}/`);
}
